import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FormContentManager from "../../components/common/form/FormContentManager";
import TableCell from "../../components/common/ui/TableCell";
import DesignTreePicker from "../../components/design/DesignTreePicker";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import * as designMeta from "../../api/design/meta";
import PeopleCell from "../../components/common/people/PeopleCell";
import { getDocPersons } from "../../api/papers";
import PeopleCellLoader from "../../components/common/people/PeopleCellLoader";

export default function Fcr_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const navigate = useNavigate();
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const reasonEditorRef = useRef(null);

  const [dns, setDns] = useState("");

  const [reviewer, setReviewer] = useState([]);

  const divisionOptions = [
    "건축",
    "구조",
    "토목",
    "설비",
    "전기",
    "소방",
    "의장",
    "기타",
  ];


  const [form, setForm] = useState({
    title: "",
    doc_type: "fcr",
    division: "",        // 공종
    area: "",            // 지역
    level: "",           // 층
    grid: "",            // 좌표
    request_date: "",    // 승인요청일
    action_date: "",     // 시행일자
    reason: "",
    attachments: [],
    details: [
      {
        id: 1,
        item: "",
        before: "",
        after: "",
        note: "",
      },
    ],
    alert: {
      send: true,
      urgent: false,
      departments: [],
    },

  });

  /* ---------------------------------------------------
     수정 모드: 기존 meta 로드
  --------------------------------------------------- */
  useEffect(() => {
    if (!paramDns) return;

    const load = async () => {
      try {
        const res = await designMeta.getMeta({ dns: paramDns });
        const meta = res.meta || res.data?.meta || {};

        const loadedPeople = await getDocPersons(paramDns);

        setDns(paramDns);
        setReviewer(reviewer);

        setForm({
          title: meta.title || "",
          doc_type: meta.doc_type || "fcr",

          division: meta.division || "",
          area: meta.area || "",
          level: meta.level || "",
          grid: meta.grid || "",
          request_date: meta.request_date || "",
          action_date: meta.action_date || "",
          reason: meta.reason || "",
          attachments: meta.attachments || [],
          details: meta.details || [],
          alert: meta.alert || {
            send: true,
            urgent: false,
            departments: loadedPeople.map((p) => p.id),
          },
        });
      } catch (e) {
        console.error(e);
        alert("기존 문서를 불러오지 못했습니다.");
      }
    };

    load();
  }, [paramDns]);

  const getBody = useCallback(() => {
    const reasonHtml = reasonEditorRef.current?.getContent() || "";

    return {
      values: {
        project: projectId,
        module: "design",
        doc_type: form.doc_type,
        dns,

        title: form.title,

        meta: {
          title: form.title,

          division: form.division,
          area: form.area,
          level: form.level,
          grid: form.grid,
          request_date: form.request_date,
          action_date: form.action_date,
          reason: reasonHtml || form.reason,

          // 🔥 PeopleCellLoader와 일치시키기 (FCN과 같은 패턴)
          reviewer: reviewer.map((p) => p.user?.id || p.id),

          // 🔥 FCN과 동일한 detail 구조로 매핑
          details: form.details.map((d) => ({
            id: d.id,
            item: d.item || "",
            before: d.before || "",
            after: d.after || "",
            note: d.note || "",

            drawingFileId: d.drawingFileId || null,
            drawingFileUrl: d.drawingFileUrl || "",

            // 화면/호환용
            drawingNo: d.item || "",
            item: d.item || "",
          })),


          // 🔥 FCN 방식 alert
          alert: {
            send: true,
            urgent: false,
            departments: reviewer.map((p) => p.user?.id || p.id),
          },

          attachments: form.attachments || [],
        },
      },
    };
  }, [form, dns, projectId, reviewer]);


  /* ---------------------------------------------------
     핸들러
  --------------------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.details];
      updated[index][key] = value;
      return { ...prev, details: updated };
    });
  };

  const addDetailRow = () =>
    setForm((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          id: prev.details.length + 1,
          item: "",
          before: "",
          after: "",
          note: "",
        }
      ],
    }));

  const removeDetailRow = (i) =>
    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, idx) => idx !== i),
    }));

  /* ---------------------------------------------------
     렌더링
  --------------------------------------------------- */
  // 요약용 요청부서 텍스트

  return (
    <FormContentManager
      params={{
        module: "design",
        doc_type: form.doc_type,
        project: projectId,
        dns,
      }}
      getBody={getBody}
      reviewer={reviewer}
      onChangeId={(newDns) => setDns(newDns)}
      redirectTo={`/project/${projectId}/design/list`}
      docName={"설계변경"}
      docDescribe={"설계 변경이 필요한 경우 아래 내용을 작성하세요."}
      summaryConfig={{
        title: "문서 요약",
        sections: [
          { label: "제목", value: form.title || "-" },
          { label: "요청부서/담당자", value: reviewer.map((r) => r.name).join(", ") || "-" },
        ],
        list: [
          {
            label: "세부 변경 내역",
            items: form.details?.map(
              (d) =>
                `${d.drawingNo || "-"}: ${d.original || "-"} → ${d.changed || "-"
                }`
            ),
          },
        ],
        footerNote: "입력한 내용이 실시간으로 반영됩니다.",
      }}
    >
      <div className="bg-white">
        {/* 상단 기본정보 */}
        <table className="w-full border text-sm mb-6 table-fixed">
          <colgroup>
            <col style={{ width: "120px" }} />
          </colgroup>
          <tbody >
            <tr >
              <TableCell
                label="문서 유형"
                editing
                colSpan={3}
                input={
                  <select
                    value={form.doc_type}
                    onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                    className="w-full border rounded px-2 py-1 text-[13px]"
                  >
                    <option value="fcr">설계변경요청서</option>
                    <option value="fcn">설계변경통보서</option>
                  </select>
                }
              />
            </tr>

            <tr >
              <TableCell
                label="제목"
                colSpan={3}
                labelClass="bg-gray-50 px-3 py-1.5 text-[13px]"
                input={
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="예: 구조벽체 두께 변경 요청"
                  />
                }
                editing
                valueClass="px-3 py-1.5 text-[13px]"
              />
            </tr>

            <tr >
              <TableCell
                label="CM 검토 담당자"
                colSpan={3}
                editing
                labelClass="bg-gray-50 px-3 py-1.5 text-[13px]"
                valueClass="px-3 py-1.5 text-[13px]"
                input={
                  <PeopleCellLoader
                    single
                    dns={paramDns}
                    value={reviewer}
                    onChange={setReviewer}
                  />
                }
              />
            </tr>
            {/* 공종 / 종류 */}
            <tr>
              <TableCell
                label="공종"
                editing
                colSpan={3}
                input={
                  <select
                    name="division"
                    value={form.division}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  >
                    <option value="">선택</option>
                    {divisionOptions.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                }
              />
            </tr>

            {/* 지역 / 층 */}
            <tr>
              <TableCell
                label="지역"
                editing
                input={
                  <input
                    name="area"
                    value={form.area}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="지역"
                  />
                }
              />
              <TableCell
                label="층"
                editing
                input={
                  <input
                    name="level"
                    value={form.level}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="층"
                  />
                }
              />
            </tr>

            {/* 좌표 */}
            <tr>
              <TableCell
                label="좌표"
                colSpan={3}
                editing
                input={
                  <input
                    name="grid"
                    value={form.grid}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="좌표"
                  />
                }
              />
            </tr>

            {/* 승인요청일 / 시행일자 */}
            <tr>
              <TableCell
                label="승인요청일"
                editing
                input={
                  <input
                    type="date"
                    name="request_date"
                    value={form.request_date}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  />
                }
              />
              <TableCell
                label="시행일자"
                editing
                input={
                  <input
                    type="date"
                    name="action_date"
                    value={form.action_date}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  />
                }
              />
            </tr>
            <tr >
              <TableCell
                label="변경 사유"
                input={
                  <TinyMCEeditor
                    height={300}
                    initialValue={form.reason}
                    editorRef={reasonEditorRef}
                  />
                }
                editing
                colSpan={3}
                labelClass="px-3 py-1.5 align-top"
                valueClass="px-3 py-1.5 align-top"
              />
            </tr>
          </tbody>
        </table>

        {/* 세부 변경 내역 */}
        <h2 className="text-body-bold mb-2">세부 변경 내역</h2>

        <div className="border-t border-r border-l border-black">
          <table className="w-full border-collapse border-black">
            <thead className="doc-thead">
              <tr>
                <th className="doc-th w-12 text-center">
                  No
                </th>
                <th className="doc-th">도면번호</th>
                <th className="doc-th">변경 전 내용</th>
                <th className="doc-th">변경 후 내용</th>
                <th className="doc-th">변경 사유</th>
                <th className="doc-th-end w-14">관리</th>
              </tr>
            </thead>

            <tbody >
              {form.details.map((row, i) => (
                <tr key={row.id} className="border-b border-black">
                  <td className="px-3 py-1.5 text-center border-r border-black">{i + 1}</td>

                  {["item", "before", "after", "note"].map((key) => (
                    <td key={key} className="px-3 py-1.5 border-r border-black">

                      {key === "item" ? (
                        // ⭐ FCN처럼 도면 선택 기능 적용
                        <div className="flex items-center gap-1">
                          {row.drawingFileId ? (
                            <div className="flex items-center justify-between w-full border rounded px-2 py-1 bg-slate-50">
                              <span className="truncate max-w-[150px] text-xs">
                                {row.item}
                              </span>
                              <button
                                onClick={() => {
                                  handleDetailChange(i, "drawingFileId", null);
                                  handleDetailChange(i, "drawingFileUrl", "");
                                  handleDetailChange(i, "item", "");
                                }}
                                className="ml-2 text-red-500 hover:text-red-600 text-xs"
                                title="선택 해제"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={row[key]}
                                onChange={(e) =>
                                  handleDetailChange(i, key, e.target.value)
                                }
                                className="w-full border rounded px-2 py-1 text-[13px]"
                                placeholder="도면번호 또는 선택"
                              />
                              <button
                                onClick={() => {
                                  setActiveRow(i);
                                  setShowDesignPicker(true);
                                }}
                                className="px-2 py-1 text-xs border rounded hover:bg-gray-100"
                                title="설계도서에서 선택"
                              >
                                📂
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={row[key]}
                          onChange={(e) =>
                            handleDetailChange(i, key, e.target.value)
                          }
                          className="w-full border rounded px-2 py-1 text-[13px]"
                        />
                      )}
                    </td>
                  ))}

                  <td className="px-3 py-1.5 text-center">
                    <button
                      onClick={() => removeDetailRow(i)}
                      className="doc-del"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-right mt-2">
          <button onClick={addDetailRow} className="btn-basic-sm">
            + 행 추가
          </button>
        </div>
      </div>
      {showDesignPicker && (
        <DesignTreePicker
          open={showDesignPicker}
          onClose={() => setShowDesignPicker(false)}
          onConfirm={(file) => {
            handleDetailChange(activeRow, "item", file.name || file.title);
            handleDetailChange(activeRow, "drawingFileId", file.id);
            handleDetailChange(
              activeRow,
              "drawingFileUrl",
              file.file_url || file.url || ""
            );
          }}
        />
      )}

    </FormContentManager>
  );
}
